
import React, { useState } from "react";
import { Modal } from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { createManualCalendarEvent } from "../../services/calendarService";
import { useToast } from "../../components/Toast";
import { DateField } from "../form/DateField";
import { formatDate, parseDate } from "../../utils/dateUtils";

type Mode = "ALL_DAY" | "TIME_SLOT";
type Recurrence = "NONE" | "WEEKLY";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:00`);
  TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:30`);
}

export function CreateCalendarEventModal({ onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [mode, setMode] = useState<Mode>("ALL_DAY");
  const [loading, setLoading] = useState(false);

  // Form Fields
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [title, setTitle] = useState("");
  const [relatedRef, setRelatedRef] = useState("");
  const [note, setNote] = useState("");
  
  // Recurrence
  const [recurrence, setRecurrence] = useState<Recurrence>("NONE");
  const [recurrenceUntil, setRecurrenceUntil] = useState("");
  
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async () => {
    setErrorMsg("");
    
    if (!title.trim()) {
      setErrorMsg("請填寫標題");
      return;
    }

    if (mode === "ALL_DAY") {
      if (!startDate || !endDate) {
        setErrorMsg("請選擇開始與結束日期");
        return;
      }
      if (endDate < startDate) {
        setErrorMsg("結束日期不可早於開始日期");
        return;
      }
    } else {
      if (!startDate) {
        setErrorMsg("請選擇日期");
        return;
      }
      if (!startTime || !endTime) {
        setErrorMsg("請選擇時間");
        return;
      }
      if (endTime <= startTime) {
        setErrorMsg("結束時間必須晚於開始時間");
        return;
      }
    }

    if (recurrence === "WEEKLY") {
        if (!recurrenceUntil) {
            setErrorMsg("請設定重複結束日期");
            return;
        }
        if (recurrenceUntil < startDate) {
            setErrorMsg("重複結束日期不可早於開始日期");
            return;
        }
    }

    setLoading(true);
    try {
      const basePayload = {
        mode,
        startTime: mode === 'TIME_SLOT' ? startTime : undefined,
        endTime: mode === 'TIME_SLOT' ? endTime : undefined,
        title,
        relatedRef,
        note,
        createdBy: user?.email || "unknown",
        recurrence,
        recurrenceUntil
      };

      await createManualCalendarEvent({
        ...basePayload,
        startDate: new Date(startDate),
        endDate: mode === 'ALL_DAY' ? new Date(endDate) : undefined,
      });
      
      showToast("事件建立成功");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      showToast("建立失敗: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="新增事件" onClose={onClose} width={500}>
      <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
        
        {/* Mode Switcher */}
        <div style={{display: 'flex', gap: 20, borderBottom: '1px solid #333', paddingBottom: 12}}>
          <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
            <input 
              type="radio" 
              checked={mode === "ALL_DAY"} 
              onChange={() => setMode("ALL_DAY")} 
            />
            <span style={{color: mode === "ALL_DAY" ? "#fff" : "#888"}}>整日區間</span>
          </label>
          <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
            <input 
              type="radio" 
              checked={mode === "TIME_SLOT"} 
              onChange={() => setMode("TIME_SLOT")} 
            />
            <span style={{color: mode === "TIME_SLOT" ? "#fff" : "#888"}}>單日特定時段</span>
          </label>
        </div>

        {/* Dynamic Fields */}
        {mode === "ALL_DAY" ? (
          <div style={{display: 'flex', gap: 12}}>
            <div style={{flex: 1}}>
              <DateField label="開始日期" required value={parseDate(startDate)} onChange={d => setStartDate(formatDate(d))} />
            </div>
            <div style={{flex: 1}}>
              <DateField label="結束日期" required value={parseDate(endDate)} onChange={d => setEndDate(formatDate(d))} />
            </div>
          </div>
        ) : (
          <div>
            <DateField label="日期" required value={parseDate(startDate)} onChange={d => setStartDate(formatDate(d))} />
            <div style={{display: 'flex', gap: 12, marginTop: 12}}>
              <div className="field" style={{flex: 1}}>
                <label className="label">開始時間 <span style={{color:'red'}}>*</span></label>
                <select className="input" value={startTime} onChange={e => setStartTime(e.target.value)}>
                  {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="field" style={{flex: 1}}>
                <label className="label">結束時間 <span style={{color:'red'}}>*</span></label>
                <select className="input" value={endTime} onChange={e => setEndTime(e.target.value)}>
                  {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="field">
          <label className="label">標題 <span style={{color:'red'}}>*</span></label>
          <input className="input" placeholder="請輸入事件標題" value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        {/* Recurrence Section */}
        <div style={{background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 8}}>
            <div style={{marginBottom: 8, fontSize: 13, color: '#aaa'}}>重複設定</div>
            <div style={{display: 'flex', gap: 20, alignItems: 'center'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                    <input type="radio" checked={recurrence === 'NONE'} onChange={() => setRecurrence('NONE')} />
                    <span>不重複</span>
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                    <input type="radio" checked={recurrence === 'WEEKLY'} onChange={() => setRecurrence('WEEKLY')} />
                    <span>每週重複</span>
                </label>
            </div>
            {recurrence === 'WEEKLY' && (
                <div style={{marginTop: 12}}>
                    <DateField 
                        label="重複直到 (結束日期)" 
                        required 
                        value={parseDate(recurrenceUntil)} 
                        onChange={d => setRecurrenceUntil(formatDate(d))} 
                    />
                </div>
            )}
        </div>

        <div className="field">
          <label className="label">關聯編號</label>
          <input className="input" placeholder="例如: CT-2023-01" value={relatedRef} onChange={e => setRelatedRef(e.target.value)} />
        </div>

        <div className="field">
          <label className="label">備註</label>
          <textarea className="input" rows={3} placeholder="請輸入備註..." value={note} onChange={e => setNote(e.target.value)} />
        </div>

        {errorMsg && <div className="err">{errorMsg}</div>}

        <div className="row" style={{marginTop: 8, justifyContent: 'flex-end'}}>
          <button className="btn" style={{width: 'auto'}} onClick={handleSubmit} disabled={loading}>
            {loading ? "建立中..." : "建立事件"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
